// Manage who else in the workspace can see threads from this account.
// Owner-only — the trigger button is hidden for non-owners.

import { useMemo } from 'react';
import { Loader2, Trash2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  useAccountShares,
  useGrantAccountShare,
  useRevokeAccountShare,
  type GmailAccount,
} from '@/lib/accounts';
import { useWorkspaceUsers } from '@/lib/collab';

interface ShareAccountDialogProps {
  account: GmailAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function initials(nameOrEmail: string): string {
  return nameOrEmail
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function ShareAccountDialog({ account, open, onOpenChange }: ShareAccountDialogProps) {
  const sharesQ = useAccountShares(open ? account.id : null);
  const wsUsersQ = useWorkspaceUsers();
  const grant = useGrantAccountShare(account.id);
  const revoke = useRevokeAccountShare(account.id);

  const owner = sharesQ.data?.owner ?? null;
  const grantees = sharesQ.data?.grantees ?? [];
  const canManage = sharesQ.data?.canManage ?? false;

  const addableUsers = useMemo(() => {
    const taken = new Set<number>();
    if (owner) taken.add(owner.id);
    for (const g of grantees) taken.add(g.id);
    return (wsUsersQ.data?.users ?? []).filter((u) => !taken.has(u.id));
  }, [owner, grantees, wsUsersQ.data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share {account.emailAddress}</DialogTitle>
          <DialogDescription>
            Pick teammates who can see and act on threads from this inbox. Only you (the
            owner) can change who has access.
          </DialogDescription>
        </DialogHeader>

        {sharesQ.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}

        {sharesQ.data && (
          <div className="space-y-3">
            {/* Owner row */}
            {owner && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                <Avatar className="size-7">
                  {owner.picture && <AvatarImage src={owner.picture} alt={owner.name} />}
                  <AvatarFallback className="text-[10px]">
                    {initials(owner.name || owner.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{owner.name || owner.email}</div>
                  <div className="truncate text-xs text-muted-foreground">{owner.email}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  Owner
                </Badge>
              </div>
            )}

            {/* Grantees */}
            {grantees.length === 0 ? (
              <p className="px-1 text-xs text-muted-foreground">
                No one else has access yet.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {grantees.map((g) => (
                  <li
                    key={g.id}
                    className="flex items-center gap-3 rounded-md border px-3 py-2"
                  >
                    <Avatar className="size-7">
                      {g.picture && <AvatarImage src={g.picture} alt={g.name} />}
                      <AvatarFallback className="text-[10px]">
                        {initials(g.name || g.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{g.name || g.email}</div>
                      <div className="truncate text-xs text-muted-foreground">{g.email}</div>
                    </div>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={() => revoke.mutate(g.id)}
                        disabled={revoke.isPending}
                        title="Remove access"
                      >
                        {revoke.isPending && revoke.variables === g.id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="size-3.5" strokeWidth={1.75} />
                        )}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Add picker (owner-only) */}
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5"
                    disabled={addableUsers.length === 0 || grant.isPending}
                  >
                    {grant.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <UserPlus className="size-3.5" strokeWidth={1.75} />
                    )}
                    {addableUsers.length === 0
                      ? 'Everyone in the workspace has access'
                      : 'Add a teammate'}
                  </Button>
                </DropdownMenuTrigger>
                {addableUsers.length > 0 && (
                  <DropdownMenuContent className="w-72">
                    {addableUsers.map((u) => (
                      <DropdownMenuItem
                        key={u.id}
                        onClick={() => grant.mutate(u.id)}
                        className="gap-2"
                      >
                        <Avatar className="size-5">
                          {u.picture && <AvatarImage src={u.picture} alt={u.name} />}
                          <AvatarFallback className="text-[8px]">
                            {initials(u.name || u.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{u.name || u.email}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
            )}

            {!canManage && (
              <p className="px-1 text-xs text-muted-foreground">
                Only the inbox owner can change who has access.
              </p>
            )}

            {(grant.error || revoke.error) && (
              <p className="text-xs text-destructive">
                {(grant.error || revoke.error) instanceof Error
                  ? (grant.error || revoke.error)!.message
                  : 'Something went wrong'}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
